import { all, call, put, select } from "redux-saga/effects";

import { isApiError } from "@mapmycustomers/shared";
import RecordSource from "@mapmycustomers/shared/enum/RecordSource";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import {
  Company,
  EntitiesSupportingRoutes,
  EntityType,
  EntityTypesSupportingRoutes,
  EntityTypeSupportingLeadGen,
  Person,
} from "@mapmycustomers/shared/types/entity";
import CompanyWithLeadId from "@mapmycustomers/shared/types/entity/CompanyWithLeadId";
import PersonWithLeadId from "@mapmycustomers/shared/types/entity/PersonWithLeadId";
import Route, {
  CreateRoutePayload,
  CreateRouteStopItem,
  RoutePreview,
  RouteStopBulkItem,
} from "@mapmycustomers/shared/types/entity/Route";
import File from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import BulkApiError from "@mapmycustomers/shared/util/api/BulkApiError";

import { CreateRouteFormPayload } from "@app/component/AddToRouteModal/store/actions";
import { getUploadedFileListIds as getCompanyFileIds } from "@app/component/createEditEntity/CreateCompanyModal/store";
import { getUploadedFileListIds as getPersonFileIds } from "@app/component/createEditEntity/CreatePersonModal/store";
import { getAddRecordsEntityType } from "@app/component/leadFinder/store/selectors";
import { convertPlaceToEntity } from "@app/component/leadFinder/utils/convertPlaceToEntity";
import { RequestOptions } from "@app/store/api/BaseApiService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import { allSettled, SettleResult } from "@app/util/effects";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";
import removeUndefinedFields from "@app/util/removeUndefinedFields";
import buildPreviewPayloadFromRoute from "@app/util/route/buildPreviewPayloadFromRoute";
import getRoutePreviewMetrics from "@app/util/route/routePreviewMetrics";

import {
  addRecordsToRoute,
  filterRoutes,
  initializeAddRecords,
  setAddRecordsVisibility,
  submitAddRecords,
  updateAddRecordsProgress,
} from "../actions";

export function* onInitializeAddRecords() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const routes: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, {
      $filters: { includeAccessStatus: true },
      $limit: 1000,
      $order: ["name"],
    });

    yield put(
      initializeAddRecords.success({
        routes: routes.data,
      })
    );
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onSubmitAddRecords({ payload }: ReturnType<typeof submitAddRecords.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const entityType: EntityTypeSupportingLeadGen = yield select(getAddRecordsEntityType);

    const fileIds: File["id"][] = yield select(
      entityType === EntityType.COMPANY ? getCompanyFileIds : getPersonFileIds
    );

    let selectedRecords: Partial<CompanyWithLeadId | PersonWithLeadId>[] =
      payload.selectedLeads.map((lead) => ({
        ...removeUndefinedFields(convertPlaceToEntity(entityType, lead)),
        ...removeUndefinedFields(payload.entity),
        files: fileIds,
        leadGenId: lead.place_id,
      }));

    yield put(setAddRecordsVisibility({ visible: false }));
    payload.onClose?.();

    const options: RequestOptions = {
      headers: {
        "x-mmc-source": RecordSource.LEAD_GEN,
      },
      params: {
        layoutId: payload.layoutId,
      },
    };

    let response: EntitiesSupportingRoutes[] = [];

    try {
      response = yield entityType === EntityType.COMPANY
        ? callApi(
            "createCompaniesInBulk",
            orgId,
            selectedRecords as Partial<CompanyWithLeadId>[],
            options
          )
        : callApi(
            "createPeopleInBulk",
            orgId,
            selectedRecords as Partial<PersonWithLeadId>[],
            options
          );
    } catch (error) {
      if (isApiError(error) && error.errors?.length && error.errors[0].validationErrors) {
        const validationErrors = error.errors[0].validationErrors;

        // leave only those records that don't have any errors in validation results
        selectedRecords = selectedRecords.filter((_, index) => {
          return !(validationErrors[index] as unknown as BulkApiError)?.errors?.length;
        });

        // and try again
        response = yield entityType === EntityType.COMPANY
          ? callApi(
              "createCompaniesInBulk",
              orgId,
              selectedRecords as Partial<CompanyWithLeadId>[],
              options
            )
          : callApi(
              "createPeopleInBulk",
              orgId,
              selectedRecords as Partial<PersonWithLeadId>[],
              options
            );
      }
    }
    const entities = response.flat();
    const entityIds = entities.map((entity: Company | Person) => entity.id);

    yield put(notifyAboutChanges({ added: entities, entityType }));

    payload.onRecordsCreated?.(entityIds, entityType);

    if (payload.customFields?.length && entityIds.length) {
      yield callApi(
        "bulkUpsertCustomFieldsValues",
        false,
        orgId,
        entityType,
        entityIds.map((id) => ({ id, layoutId: payload.layoutId })),
        payload.customFields
      );
    }

    yield put(
      updateAddRecordsProgress({
        color: payload.entity.color || undefined,
        entityIds,
      })
    );

    if (entityType === EntityType.COMPANY && payload.personId) {
      const person: Person | undefined = yield callApi("fetchPerson", orgId, payload.personId);
      if (person) {
        yield callApi("updatePerson", orgId, undefined, {
          id: person.id,
          accounts: [...person.accounts, ...entityIds.map((id) => ({ id }))],
        });
      }
    }

    if (payload.groupIds?.length) {
      yield allSettled(
        payload.groupIds.map((groupId) =>
          callApi("addToGroup", orgId, groupId, entityType, entityIds)
        )
      );

      yield put(
        updateAddRecordsProgress({
          groupIds: payload.groupIds,
        })
      );
    }

    if (payload.routeIdsOrRoutePayloads?.length) {
      const newRoutePayloads = payload.routeIdsOrRoutePayloads.filter(
        (value) => typeof value !== "number"
      ) as CreateRouteFormPayload[];
      const newRoutesResult: SettleResult<Route>[] = yield allSettled(
        newRoutePayloads.map(createRoute)
      );
      const newRouteIds = newRoutesResult
        .filter(({ error }) => !error)
        .map(({ result }) => (result as Route).id);

      yield all(
        [
          ...newRouteIds,
          ...(payload.routeIdsOrRoutePayloads.filter(
            (value) => typeof value === "number"
          ) as Route["id"][]),
        ].map((routeId) =>
          call(
            submitAddRecordsToRoute,
            routeId,
            entityIds,
            entityType,
            newRouteIds.includes(routeId)
          )
        )
      );
      if (newRouteIds.length) {
        yield put(initializeAddRecords.request({ entityType }));
      }
    }

    yield put(submitAddRecords.success());

    payload.onComplete?.();
  } catch (error) {
    yield put(submitAddRecords.failure());
    yield put(handleError({ error }));
  }
}

export function* createRoute(route: CreateRouteFormPayload) {
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const startCoordsResult: GeocodeResult = yield callApi("geocodeAddress", orgId, {
    address: route.routeDetail.startAddress,
    city: null,
    country: null,
    postalCode: null,
    region: null,
  });

  let endCoordsResult: GeocodeResult | undefined = undefined;
  if (route.routeDetail.endAddress) {
    endCoordsResult = yield callApi("geocodeAddress", orgId, {
      address: route.routeDetail.endAddress,
      city: null,
      country: null,
      postalCode: null,
      region: null,
    });
  }

  const createPayload: CreateRoutePayload = {
    name: route.name,
    routeDetail: {
      allottedTime: route.routeDetail.allottedTime,
      startAddress: route.routeDetail.startAddress,
      startAt: route.routeDetail.startAt,
      startGeoPoint: startCoordsResult.geoPoint!,
      travelMode: route.routeDetail.travelMode,
      type: route.routeDetail.type,
      ...(endCoordsResult && route.routeDetail.endAddress
        ? {
            endAddress: route.routeDetail.endAddress,
            endGeoPoint: endCoordsResult.geoPoint,
          }
        : {}),
    },
  };

  const createdRoute: Route = yield callApi("createUnifiedRoute", orgId, createPayload);

  return createdRoute;
}

function* submitAddRecordsToRoute(
  routeId: Route["id"],
  entityIds: EntitiesSupportingRoutes["id"][],
  entityType: EntityTypesSupportingRoutes,
  isNewRoute?: boolean
) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const route: Route = yield callApi("fetchUnifiedRoute", orgId, routeId);

    const existingEntityIds = new Set(
      (route.stopTime ?? []).flatMap((stop) => {
        if (entityType === EntityType.COMPANY && stop.entity === EntityType.COMPANY) {
          return [stop.accountId];
        }
        if (entityType === EntityType.PERSON && stop.entity === EntityType.PERSON) {
          return [stop.contactId];
        }
        return [];
      })
    );
    const deduplicatedNewIds = entityIds.filter((id) => !existingEntityIds.has(id));
    const newStopItems: CreateRouteStopItem[] =
      entityType === EntityType.COMPANY
        ? deduplicatedNewIds.map((id) => ({
            accountId: id,
            allottedTime: route.routeDetail.allottedTime ?? 0,
          }))
        : deduplicatedNewIds.map((id) => ({
            allottedTime: route.routeDetail.allottedTime ?? 0,
            contactId: id,
          }));
    // Build the existing-stop portion through the shared helper so the checked-off filter
    // and the routeDetail shaping both come from one place, same as bulkAddEntitiesToRoutes
    // (component/view/SelectBar/store/saga.ts).
    const basePayload = buildPreviewPayloadFromRoute(route);
    const allStopItems: CreateRouteStopItem[] = [...basePayload.stopTime, ...newStopItems];

    const preview: RoutePreview = yield callApi("previewUnifiedRoute", orgId, {
      routeDetail: basePayload.routeDetail,
      stopTime: allStopItems,
    });

    const appendOps: RouteStopBulkItem[] =
      entityType === EntityType.COMPANY
        ? deduplicatedNewIds.map((id) => ({
            _action_: "c",
            accountId: id,
            allottedTime: route.routeDetail.allottedTime ?? 0,
          }))
        : deduplicatedNewIds.map((id) => ({
            _action_: "c",
            allottedTime: route.routeDetail.allottedTime ?? 0,
            contactId: id,
          }));

    yield callApi(
      "bulkUpdateUnifiedRouteStops",
      orgId,
      routeId,
      appendOps,
      getRoutePreviewMetrics(preview)
    );

    yield put(
      addRecordsToRoute.success({
        isNewRoute,
        routeId: routeId,
      })
    );
  } catch (error) {
    const errors = (getValidationErrors(error) ?? []).map((error) => error.message);
    if (errors.length) {
      // Do not display API error notification for validation failures, because they are actually displayed in modal
      yield put(
        addRecordsToRoute.failure({
          errors,
          routeId: routeId,
        })
      );
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onFilterRoutes({ payload }: ReturnType<typeof filterRoutes.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const routes: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, {
      $filters: {
        includeAccessStatus: true,
        name: payload.query ? { $in: payload.query } : undefined,
      },
      $limit: 100,
      $order: ["name"],
    });
    yield put(filterRoutes.success(routes.data));
  } catch (error) {
    yield put(filterRoutes.failure());
    yield put(handleError({ error }));
  }
}
